import React, {useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import styles from './Videos.module.css';
import EmptyState from '../patterns/EmptyState';
import {PanelEmptyIllustration} from '../patterns/EmptyStateIllustrations';
import useSmartBack from '../app-shell/useSmartBack';
import {PlayIcon, ArrowRightIcon, MailIcon} from '../marketing/icons/Icons';
import {HOW_TO_ROUTE, CONTACT_ROUTE} from '../marketing/routes';

// Local to this page, like the small icon components elsewhere in the app.
const BackArrowIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M19 12H5M11 6l-6 6 6 6"/>
    </svg>
);

const ExternalLinkIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M14 4h6v6M20 4l-9 9M18 14v5H5V6h5"/>
    </svg>
);

// Placeholder poster art (an ovals drawing standing in for a real still) -
// see the "Placeholders in the designs" note in the build brief.
const PosterArt = () => (
    <svg viewBox="0 0 776 440" preserveAspectRatio="xMidYMid slice" aria-hidden="true" className={styles.posterArt}>
        <g opacity="0.5" stroke="#3A3A40" strokeWidth="2">
            <line x1="220" y1="60" x2="220" y2="400"/>
            <line x1="388" y1="60" x2="388" y2="400"/>
            <line x1="556" y1="60" x2="556" y2="400"/>
        </g>
        <g fill="none" stroke="#6B6A72" strokeWidth="2">
            <path d="M288 120 C 330 120, 330 180, 318 180"/>
            <path d="M456 190 C 500 190, 500 250, 486 250"/>
        </g>
        <ellipse cx="220" cy="120" rx="68" ry="26" fill="none" stroke="#30A46C" strokeWidth="3" opacity="0.8"/>
        <ellipse cx="388" cy="186" rx="68" ry="26" fill="none" stroke="#F5A524" strokeWidth="3" opacity="0.8"/>
        <ellipse cx="556" cy="256" rx="68" ry="26" fill="none" stroke="#E5484D" strokeWidth="3" opacity="0.8"/>
        <ellipse cx="388" cy="330" rx="68" ry="26" fill="none" stroke="#E5484D" strokeWidth="3" opacity="0.8"/>
    </svg>
);

// Guides come from one config array so a new guide needs no code changes.
// Duration/thumbnail are placeholders per the build brief's open questions -
// there's no real video metadata source wired up yet.
const GUIDES = [
    {
        id: 'what-is-ovalview',
        youtubeId: 'fZ-OOunODtc',
        title: 'What is Ovalview?',
        description: 'An introduction to Ovalview.',
        duration: '0:00',
    },
];

// A lightweight YouTube facade: shows a poster with a play button until
// clicked, only then loading the (privacy-enhanced, nocookie) iframe. Keyed
// by guide id from the parent so switching guides remounts this with a
// fresh, un-played poster.
const FeaturedVideo = ({guide}) => {
    const [playing, setPlaying] = useState(false);

    if (playing) {
        return (
            <div className={styles.player}>
                <iframe
                    className={styles.playerFrame}
                    src={`https://www.youtube-nocookie.com/embed/${guide.youtubeId}?autoplay=1`}
                    title={guide.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
        );
    }

    return (
        <button type="button" className={styles.player} aria-label={`Play video: ${guide.title}`}
                onClick={() => setPlaying(true)}>
            <PosterArt/>
            <span className={styles.playButton}><PlayIcon width={28} height={28} fill="#141416"/></span>
            <span className={styles.introChip}>Introduction</span>
            <span className={styles.durationChip}>[{guide.duration}]</span>
        </button>
    );
};

const Videos = ({guides = GUIDES}) => {
    const location = useLocation();
    const goBack = useSmartBack();

    const params = new URLSearchParams(location.search);
    const requestedId = params.get('v');
    const activeGuide = guides.find((g) => g.id === requestedId) || guides[0];

    return (
        <div className={styles.root}>
            <Helmet>
                <title>Ovalview | Video guides</title>
            </Helmet>
            <div className={styles.page}>
                <button type="button" className={styles.backLink} onClick={goBack}>
                    <BackArrowIcon/>Back
                </button>

                <div className={styles.header}>
                    <p className={styles.eyebrow}>HOW TO</p>
                    <h1 className={styles.title}>Video guides</h1>
                    <p className={styles.subtitle}>Brief videos to get you started.</p>
                </div>

                {guides.length === 0 ? (
                    <EmptyState
                        size="panel"
                        illustration={<PanelEmptyIllustration/>}
                        title="No guides yet"
                        description="Check back soon, or read the quick start guide instead."
                        primaryAction={<Link to={HOW_TO_ROUTE} className={styles.readGuideLink}>Read the quick start guide</Link>}
                    />
                ) : (
                    <div className={styles.grid}>
                        <article aria-labelledby="featured-video-title" className={styles.videoColumn}>
                            <FeaturedVideo key={activeGuide.id} guide={activeGuide}/>
                            <div className={styles.videoMeta}>
                                <div className={styles.videoMetaText}>
                                    <h2 id="featured-video-title" className={styles.videoTitle}>{activeGuide.title}</h2>
                                    <p className={styles.videoDescription}>{activeGuide.description}</p>
                                </div>
                                <a
                                    href={`https://www.youtube.com/watch?v=${activeGuide.youtubeId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.youtubeLink}
                                >
                                    Open on YouTube<ExternalLinkIcon/>
                                    <span className={styles.srOnly}> (opens in a new tab)</span>
                                </a>
                            </div>
                        </article>

                        <aside className={styles.sideColumn}>
                            <section aria-labelledby="all-guides-heading" className={styles.guidesCard}>
                                <div className={styles.guidesHeader}>
                                    <h2 id="all-guides-heading" className={styles.guidesHeading}>ALL GUIDES</h2>
                                    <span className={styles.guidesCount}>{guides.length} video{guides.length === 1 ? '' : 's'}</span>
                                </div>
                                <ul className={styles.guidesList}>
                                    {guides.map((guide) => {
                                        const current = guide.id === activeGuide.id;
                                        return (
                                            <li key={guide.id}>
                                                <Link
                                                    to={{pathname: location.pathname, search: `?v=${guide.id}`}}
                                                    aria-current={current ? 'true' : undefined}
                                                    className={`${styles.guideRow} ${current ? styles.guideRowActive : ''}`}
                                                >
                                                    <span className={styles.guideThumb}><PlayIcon width={14} height={14} fill="#FFFFFF"/></span>
                                                    <span className={styles.guideText}>
                                                        <span className={styles.guideTitle}>{guide.title}</span>
                                                        <span className={styles.guideMeta}>
                                                            {current ? <>Now showing · <span className={styles.guideDuration}>[{guide.duration}]</span></> : `[${guide.duration}]`}
                                                        </span>
                                                    </span>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {guides.length === 1 && <p className={styles.guidesFooter}>More guides are on the way.</p>}
                            </section>

                            <Link to={HOW_TO_ROUTE} className={styles.linkCard}>
                                <span className={styles.linkCardText}>
                                    <span className={styles.linkCardEyebrow}>Prefer reading?</span>
                                    <span className={styles.linkCardTitle}>Read the quick start guide</span>
                                </span>
                                <span className={styles.linkCardIcon}><ArrowRightIcon width={16} height={16}/></span>
                            </Link>

                            <Link to={CONTACT_ROUTE} className={`${styles.linkCard} ${styles.linkCardBordered}`}>
                                <span className={styles.linkCardText}>
                                    <span className={styles.linkCardEyebrow}>Still stuck?</span>
                                    <span className={styles.linkCardTitle}>Contact us</span>
                                </span>
                                <span className={`${styles.linkCardIcon} ${styles.linkCardIconMuted}`}><MailIcon width={18} height={18}/></span>
                            </Link>
                        </aside>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Videos;
